import { gql, useMutation } from "@apollo/client";
import { client } from "../apolloClient";

/**
 * useClientDesignationApi custom hook for client designation API operations.
 * - Provides CRUD operations for client designations using Apollo Client and GraphQL.
 * - Handles cache updates for client designation queries and mutations.
 * - Exposes helpers for reading/writing/updating client designations in Apollo cache.
 * - Returns methods for fetching, creating, updating, and deleting client designations.
 * - Ensures UI stays in sync with backend by updating Apollo cache after mutations.
 */

// GraphQL Queries and Mutations
const CLIENT_DESIGNATION_QUERIES = {
  // Query to fetch all client designations
  GET_CLIENT_DESIGNATIONS: gql`
    query GetClientDesignations {
      GetClientDesignations {
        client_designation_id
        client_designation_name
        is_active
        created_on
        updated_on
      }
    }
  `,

  // Query to fetch a client designation by ID
  GET_CLIENT_DESIGNATION_BY_ID: gql`
    query GetClientDesignationById {
      GetClientDesignationById {
        client_designation_id
        client_designation_name
        is_active
        created_on
        updated_on
      }
    }
  `,

  // Mutation to add a new client designation
  ADD_CLIENT_DESIGNATION: gql`
    mutation AddClientDesignation($clientDesignationName: String!) {
      AddClientDesignation(client_designation_name: $clientDesignationName) {
        client_designation_id
        client_designation_name
        is_active
        created_on
        updated_on
      }
    }
  `,

  // Mutation to update an existing client designation
  UPDATE_CLIENT_DESIGNATION: gql`
    mutation UpdateClientDesignation(
      $clientDesignationId: ID!
      $clientDesignationName: String
    ) {
      UpdateClientDesignation(
        client_designation_id: $clientDesignationId
        client_designation_name: $clientDesignationName
      ) {
        client_designation_id
        client_designation_name
        is_active
        created_on
        updated_on
      }
    }
  `,

  // Mutation to delete a client designation
  DELETE_CLIENT_DESIGNATION: gql`
    mutation DeleteClientDesignation($clientDesignationId: ID!) {
      DeleteClientDesignation(client_designation_id: $clientDesignationId) {
        client_designation_id
        client_designation_name
        is_active
        created_on
        updated_on
      }
    }
  `,
};

/**
 * Cache operations for client designations.
 * - readClientDesignations: Reads client designations from Apollo cache.
 * - writeClientDesignations: Writes client designations to Apollo cache.
 * - updateClientDesignationInCache: Updates a client designation in Apollo cache.
 */
const cacheOperations = {
  readClientDesignations: () => {
    try {
      const { GetClientDesignations = [] } =
        client.readQuery({
          query: CLIENT_DESIGNATION_QUERIES.GET_CLIENT_DESIGNATIONS,
        }) || {};
      return GetClientDesignations;
    } catch {
      return [];
    }
  },
  writeClientDesignations: (clientDesignations) => {
    client.writeQuery({
      query: CLIENT_DESIGNATION_QUERIES.GET_CLIENT_DESIGNATIONS,
      data: { GetClientDesignations: clientDesignations },
    });
  },
  updateClientDesignationInCache: (updatedClientDesignation) => {
    const existingClientDesignations = cacheOperations.readClientDesignations();
    const updatedClientDesignations = existingClientDesignations.map(
      (clientDesignation) =>
        clientDesignation.client_designation_id ===
        updatedClientDesignation.client_designation_id
          ? updatedClientDesignation
          : clientDesignation
    );
    cacheOperations.writeClientDesignations(updatedClientDesignations);
  },
};

export const useClientDesignationApi = () => {
  // Mutation for adding a client designation
  const [addClientDesignationMutation] = useMutation(
    CLIENT_DESIGNATION_QUERIES.ADD_CLIENT_DESIGNATION,
    {
      update(cache, { data: { AddClientDesignation } }) {
        try {
          const existingData = cache.readQuery({
            query: CLIENT_DESIGNATION_QUERIES.GET_CLIENT_DESIGNATIONS,
          });
          if (existingData) {
            cache.writeQuery({
              query: CLIENT_DESIGNATION_QUERIES.GET_CLIENT_DESIGNATIONS,
              data: {
                GetClientDesignations: [
                  AddClientDesignation,
                  ...existingData.GetClientDesignations,
                ],
              },
            });
          }
        } catch (error) {
          console.error(
            "Error updating cache after adding client designation :",
            error
          );
        }
      },
    }
  );

  // Mutation for updating a client designation
  const [updateClientDesignationMutation] = useMutation(
    CLIENT_DESIGNATION_QUERIES.UPDATE_CLIENT_DESIGNATION,
    {
      update(cache, { data: { UpdateClientDesignation } }) {
        try {
          const existingData = cache.readQuery({
            query: CLIENT_DESIGNATION_QUERIES.GET_CLIENT_DESIGNATIONS,
          });
          if (existingData) {
            const newData = existingData.GetClientDesignations.map(
              (clientDesignation) =>
                clientDesignation.client_designation_id ===
                UpdateClientDesignation.client_designation_id
                  ? UpdateClientDesignation
                  : clientDesignation
            );
            cache.writeQuery({
              query: CLIENT_DESIGNATION_QUERIES.GET_CLIENT_DESIGNATIONS,
              data: { GetClientDesignations: newData },
            });
          }
        } catch (error) {
          console.error(
            "Error updating cache after updating client designation:",
            error
          );
        }
      },
    }
  );

  // Mutation for deleting a client designation
  const [deleteClientDesignationMutation] = useMutation(
    CLIENT_DESIGNATION_QUERIES.DELETE_CLIENT_DESIGNATION,
    {
      update(cache, { data: { DeleteClientDesignation } }) {
        try {
          const existingData = cache.readQuery({
            query: CLIENT_DESIGNATION_QUERIES.GET_CLIENT_DESIGNATIONS,
          });
          if (existingData) {
            cache.writeQuery({
              query: CLIENT_DESIGNATION_QUERIES.GET_CLIENT_DESIGNATIONS,
              data: {
                GetClientDesignations:
                  existingData.GetClientDesignations.filter(
                    (clientDesignation) =>
                      clientDesignation.client_designation_id !==
                      DeleteClientDesignation.client_designation_id
                  ),
              },
            });
          }
        } catch (error) {
          console.error(
            "Error updating cache after deleting client designation:",
            error
          );
        }
      },
    }
  );

  /**
   * Fetches all client designations from the backend and updates the cache.
   * @returns {Promise<{success: boolean, data?: Array, error?: string}>}
   */
  const getClientDesignations = async () => {
    try {
      console.log("Calling getClientDesignations API...");
      const { data } = await client.query({
        query: CLIENT_DESIGNATION_QUERIES.GET_CLIENT_DESIGNATIONS,
        fetchPolicy: "network-only",
        nextFetchPolicy: "cache-first",
      });
      console.log("getClientDesignations response:", data);

      if (!data || !data.GetClientDesignations) {
        throw new Error("No data received from getClientDesignations");
      }

      // Update cache
      client.writeQuery({
        query: CLIENT_DESIGNATION_QUERIES.GET_CLIENT_DESIGNATIONS,
        data: { GetClientDesignations: data.GetClientDesignations },
      });
      return { success: true, data: data.GetClientDesignations };
    } catch (error) {
      console.error("Error in getClientDesignations:", error);
      return { success: false, error: error.message };
    }
  };

  /**
   * Fetches a client designation by its ID.
   * @param {string|number} client_designation_id
   * @returns {Promise<{success: boolean, data?: Object, error?: string}>}
   */
  const getClientDesignationById = async (client_designation_id) => {
    try {
      const { data } = await client.query({
        query: CLIENT_DESIGNATION_QUERIES.GET_CLIENT_DESIGNATION_BY_ID,
        variables: { clientDesignationId: client_designation_id },
        fetchPolicy: "network-only",
      });
      return { success: true, data: data.GetClientDesignationById };
    } catch (error) {
      console.error("Error fetching client designation:", error);
      return { success: false, error: error.message };
    }
  };

  /**
   * Creates a new client designation.
   * @param {Object} formData - { client_designation_name: string }
   * @returns {Promise<{success: boolean, data?: Object, error?: string}>}
   */
  const createClientDesignation = async (formData) => {
    try {
      const { data } = await addClientDesignationMutation({
        variables: {
          clientDesignationName: formData.client_designation_name,
        },
      });
      // Return the new client designation directly from mutation response
      return { success: true, data: data.AddClientDesignation };
    } catch (error) {
      console.error("Error creating client designation:", error);
      return { success: false, error: error.message };
    }
  };

  /**
   * Updates an existing client designation.
   * @param {string|number} clientDesignationId
   * @param {Object} formData - { client_designation_name: string }
   * @returns {Promise<{success: boolean, data?: Object, error?: string}>}
   */
  const updateClientDesignation = async (clientDesignationId, formData) => {
    try {
      const { data } = await updateClientDesignationMutation({
        variables: {
          clientDesignationId: clientDesignationId,
          clientDesignationName: formData.client_designation_name,
        },
      });
      // Return the updated client designation directly from mutation response
      return { success: true, data: data.UpdateClientDesignation };
    } catch (error) {
      console.error("Error updating client designation:", error);
      return { success: false, error: error.message };
    }
  };

  /**
   * Deletes a client designation by its ID.
   * @param {string|number} client_designation_id
   * @returns {Promise<{success: boolean, data?: Object, error?: string}>}
   */
  const deleteClientDesignation = async (client_designation_id) => {
    try {
      const { data } = await deleteClientDesignationMutation({
        variables: { clientDesignationId: client_designation_id },
      });

      return { success: true, data: data.DeleteClientDesignation };
    } catch (error) {
      console.error("Error deleting client designation:", error);
      return { success: false, error: error.message };
    }
  };

  return {
    getClientDesignations,
    getClientDesignationById,
    createClientDesignation,
    updateClientDesignation,
    deleteClientDesignation,
    cacheOperations,
  };
};
